import React from 'react';
import { AppBar, Toolbar, Typography, Box, useTheme, Button, IconButton, Menu, MenuItem, Tooltip, alpha } from '@mui/material';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import GitHubIcon from '@mui/icons-material/GitHub';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import ScienceIcon from '@mui/icons-material/Science';
import { motion } from 'framer-motion';
import { useThemeContext } from '../contexts/ThemeContext';
import LogoImage from '../assets/images/logo.png';
import RateReviewIcon from '@mui/icons-material/RateReview';

function Header() {
  const theme = useTheme();
  const { isDarkMode, toggleDarkMode } = useThemeContext();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Generate gradient based on theme with fallback colors
  const getGradient = () => {
    const primaryColor = theme?.palette?.primary?.main || '#4f46e5';
    const secondaryColor = theme?.palette?.secondary?.main || '#0ea5e9';
    const tertiaryColor = theme?.palette?.tertiary?.main || '#8b5cf6';

    return isDarkMode
      ? `linear-gradient(135deg, ${primaryColor} 0%, ${tertiaryColor} 100%)`
      : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`;
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: isDarkMode
          ? alpha(theme.palette.background.paper, 0.8)
          : alpha(theme.palette.background.paper, 0.8),
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
        backdropFilter: 'blur(10px)',
        zIndex: theme.zIndex.drawer + 1,
        transition: 'all 0.3s ease',
      }}
    >
      <Toolbar sx={{ padding: { xs: '0.25rem 1rem', sm: '0.25rem 1.5rem' }, minHeight: '56px' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          {/* Logo Section */}
          <Box display="flex" alignItems="center">
            <motion.div
              whileHover={{
                scale: 1.05,
                filter: 'brightness(1.1)',
                transition: {
                  type: 'spring',
                  stiffness: 200,
                  damping: 30,
                  duration: 0.01
                }
              }}
              style={{
                position: 'relative',
                zIndex: 1
              }}
            >
              <Box
                sx={{
                  borderRadius: theme.shape.borderRadius,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5,
                  boxShadow: isDarkMode
                    ? `0 0 20px ${alpha(theme.palette.primary.main, 0.4)}`
                    : `0 0 20px ${alpha(theme.palette.primary.main, 0.25)}`,
                  transition: 'all 0.25s ease-out',
                  width: 36,
                  height: 36,
                  overflow: 'hidden',
                  position: 'relative',
                  '&:hover': {
                    boxShadow: `0 0 24px 4px ${alpha('#0085FF', 0.6)}`,
                    '&::after': {
                      opacity: 0.7,
                    }
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle, rgba(0,133,255,0.3) 0%, rgba(0,0,0,0) 70%)',
                    opacity: 0,
                    transition: 'opacity 0.25s ease-out'
                  }
                }}
                className="pulse-subtle"
              >
                <img
                  src={LogoImage}
                  alt="LitLLM Logo"
                  style={{
                    width: '110%',
                    height: '110%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    position: 'relative',
                    zIndex: 2,
                    transform: 'scale(1.1)',
                    transformOrigin: 'center'
                  }}
                />
              </Box>
            </motion.div>
            <Box>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.025em',
                  background: getGradient(),
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: isDarkMode
                    ? `0 2px 10px ${alpha(theme.palette.primary.main, 0.4)}`
                    : `0 2px 10px ${alpha(theme.palette.primary.main, 0.2)}`,
                  transition: 'all 0.3s ease'
                }}
              >
                LitLLM
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  color: theme.palette.text.secondary,
                  letterSpacing: '0.01em',
                  fontWeight: 500,
                  transition: 'color 0.3s ease',
                  fontSize: '0.85rem',
                  display: 'block',
                  marginTop: '-2px'
                }}
              >
                Scientific Literature Review
              </Typography>
            </Box>
          </Box>

          {/* Navigation & Action Buttons */}
          <Box display={{ xs: 'none', md: 'flex' }} alignItems="center" gap={1}>
            {/* <Button 
              variant="text" 
              startIcon={<SettingsSuggestIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleMenuOpen}
              sx={{ 
                color: theme.palette.text.primary,
                fontWeight: 500,
                borderRadius: '8px',
                padding: '8px 16px',
                '&:hover': { 
                  backgroundColor: alpha(theme.palette.primary.main, 0.08) 
                },
                transition: 'all 0.2s ease'
              }}
            >
              Options
            </Button> */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                elevation: 3,
                sx: {
                  borderRadius: '10px',
                  minWidth: 200,
                  mt: 1.5,
                  overflow: 'hidden',
                  padding: '4px',
                  boxShadow: isDarkMode
                    ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                    : '0 4px 20px rgba(0, 0, 0, 0.15)'
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleMenuClose} sx={{
                borderRadius: '6px',
                margin: '2px 0',
                fontSize: '0.95rem',
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
              }}>
                <SettingsSuggestIcon fontSize="small" sx={{ mr: 1.5, color: theme.palette.primary.main }} />
                Model Settings
              </MenuItem>
              <MenuItem onClick={handleMenuClose} sx={{
                borderRadius: '6px',
                margin: '2px 0',
                fontSize: '0.95rem',
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
              }}>
                API Keys
              </MenuItem>
              <MenuItem onClick={handleMenuClose} sx={{
                borderRadius: '6px',
                margin: '2px 0',
                fontSize: '0.95rem',
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
              }}>
                Preferences
              </MenuItem>
            </Menu>

            {/* <Tooltip title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
              <IconButton
                onClick={toggleDarkMode}
                sx={{
                  color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: '8px',
                  padding: '8px',
                  ml: 1,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.2s ease'
                }}
                className="scale-hover"
              >
                {isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
              </IconButton>
            </Tooltip> */}

            <Box
              component="a"
              href="https://forms.gle/gHnJXTRbTk9MwVR88"
              target="_blank"
              sx={{ ml: 1, textDecoration: 'none' }}
            >
              <Tooltip title="Give Feedback">
                <IconButton
                  sx={{
                    color: theme.palette.text.secondary,
                    backgroundColor: alpha(theme.palette.divider, 0.2),
                    borderRadius: '8px',
                    padding: '8px',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.divider, 0.3),
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                  className="scale-hover"
                >
                  <RateReviewIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Box
              component="a"
              href="https://github.com/LitLLM/LitLLM/"
              target="_blank"
              sx={{ ml: 1, textDecoration: 'none' }}
            >
              <Tooltip title="View on GitHub">
                <IconButton
                  sx={{
                    color: theme.palette.text.secondary,
                    backgroundColor: alpha(theme.palette.divider, 0.2),
                    borderRadius: '8px',
                    padding: '8px',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.divider, 0.3),
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                  className="scale-hover"
                >
                  <GitHubIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Mobile Menu */}
          <Box display={{ xs: 'flex', md: 'none' }} alignItems="center" gap={1}>
            <Tooltip title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
              <IconButton
                onClick={toggleDarkMode}
                sx={{
                  color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.15) }
                }}
              >
                {isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
              </IconButton>
            </Tooltip>

            <IconButton
              sx={{
                color: theme.palette.text.primary,
                ml: 1
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default Header; 